import { useState } from "react";
import { formatNumber, formatCurrency, formatPercent } from "utils/formatNumber";
import { Row, Col, Card } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { type DashboardStats, type PlanStrategyPlanList, type ScatterStats, type Limits } from "../../PricingPlanTable";
import { trellisPalette } from "components/custom/analytics/palettes";
import { IoArrowUpCircleSharp, IoArrowDownCircleSharp } from "react-icons/io5";
import Loading from "components/core/blocks/Loading";
import moment from "moment";
import { FaExpandArrowsAlt } from "react-icons/fa";
import { BiCollapse } from "react-icons/bi";
import { Flex } from "../../../../../styled-system/jsx";
import { type GroupedStats } from "modules/pricingv2/pricingTypes";

interface Props {
  dashboardData:
    | {
        total: DashboardStats;
        date: DashboardStats[];
        plan_id: DashboardStats[];
        plan_strategies: PlanStrategyPlanList;
      }
    | undefined;
  isLoading: boolean;
  marketPlace: string;
  pricingPlan: {
    enroll_date: string;
  };
  fetching: boolean;
  singlePlan: boolean;
  scatterData: ScatterStats;
  setXYLimit?: (limits: Limits | null) => void;
  selectedPlanStrategy: string;
  inventoryMetrics: GroupedStats;
  setHealthSelection: (health: string | undefined) => void;
  healthSelection: string | undefined;
}
interface ChartProps {
  dashboardData: {
    date: DashboardStats[];
    plan_id: DashboardStats[];
  };
  isLoading: boolean;
  marketPlace?: string;
  enrollDate?: string;
  isExpanded?: boolean;
  scatterData?: ScatterStats;
  setXYLimit?: (limits: Limits | null) => void;
  singlePlan?: boolean;
}

const SimpleChart = ({
  dashboardData,
  isLoading,
  marketPlace,
  enrollDate,
  isExpanded,
  scatterData,
  setXYLimit,
  singlePlan,
}: ChartProps) => {
  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  if (scatterData !== undefined) {
    return (
      <ScatterChart
        dashboardData={dashboardData}
        isLoading={isLoading}
        marketPlace={marketPlace}
        isExpanded={isExpanded}
        scatterData={scatterData}
        setXYLimit={setXYLimit}
        singlePlan={singlePlan}
      />
    );
  }
  return <IncrementalGCDChart dashboardData={dashboardData} isLoading={isLoading} enrollDate={enrollDate} />;
};

const ScatterChart = ({ isLoading, marketPlace, isExpanded, scatterData, setXYLimit, singlePlan }: ChartProps) => {
  const [regionSet, setRegionSet] = useState(-1);

  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  const scatterPoints = scatterData?.scatterData ?? [];
  const centerX = 0;
  const centerY = 0;
  const minX = scatterData?.minX || 0;
  const maxX = scatterData?.maxX || 0;
  const minY = scatterData?.minY || 0;
  const maxY = scatterData?.maxY || 0;

  // if (scatterPoints.length > 1000) {
  //   scatterPoints = scatterPoints.slice(0, 1000);
  // }

  let chartHeight = 435;
  if (isExpanded) {
    chartHeight = 600;
  } else if (singlePlan) {
    chartHeight = 335;
  }

  let markerRadius = 4;
  if (scatterPoints.length > 1000) {
    markerRadius = 2;
  }

  const series: Highcharts.SeriesOptionsType[] = [
    {
      type: "polygon",
      name: "Products in this quadrant have experienced a rise in units sold and a gain in incremental GCD revenue.",
      color: "rgba(0, 128, 0, 0.25)",
      data: [
        [centerX, centerY],
        [maxX, centerY],
        [maxX, maxY],
        [centerX, maxY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: "Products in this quadrant have experienced a decline in units sold and incurred some GCD cost.",
      color: "rgba(178, 34, 34, 0.25)",
      data: [
        [centerX, centerY],
        [minX, centerY],
        [minX, minY],
        [centerX, minY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: "Products in this quadrant have experienced a rise in units sold and a gain in incremental GCD revenue.",
      color: "rgba(255, 165, 0, 0.25)",
      data: [
        [centerX, centerY],
        [minX, centerY],
        [minX, maxY],
        [centerX, maxY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      type: "polygon",
      name: "Products in this quadrant have experienced a decline in units sold and a gain in incremental GCD revenue.",
      color: "rgba(0, 120, 215, 0.25)",
      data: [
        [centerX, centerY],
        [maxX, centerY],
        [maxX, minY],
        [centerX, minY],
      ],
      enableMouseTracking: true,
      showInLegend: false,
      lineWidth: 0,
    },
    {
      name: "Pricing Plans",
      type: "scatter",
      color: trellisPalette[3],
      data: scatterPoints,
      showInLegend: false,
      marker: {
        symbol: "circle",
        radius: markerRadius,
      },
      turboThreshold: 0,
    },
  ];

  const scatterOptions: Highcharts.Options = {
    chart: {
      type: "scatter",
      zoomType: "xy",
      height: chartHeight,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    } as Highcharts.ChartOptions,
    title: {
      text: undefined,
    },
    xAxis: {
      min: minX,
      max: maxX,
      title: { text: "Incremental GCD from Price Increases" },
    },
    yAxis: {
      min: minY,
      max: maxY,
      title: { text: "Incremental Units" },
    },
    tooltip: {
      useHTML: true,
      formatter: function (this) {
        if (this.series.index <= 3) {
          return `${this.series.name}`;
        }

        return `<strong>${this.key}</strong><br/>Inc. GCD: ${formatCurrency(this.x, marketPlace)}<br/>Inc. Units: ${formatNumber(this.y as number, { decimalPlaces: 0 })}`.toString();
      },
    },
    plotOptions: {
      series: {
        point: {
          events: {
            click: function (this: Highcharts.Point): void {
              if (this.series.index <= 3) {
                let maxX = 0;
                let maxY = 0;
                let minX = 0;
                let minY = 0;
                for (let i = 0; i < this.series.data.length; i++) {
                  const x = this.series.data[i]?.x ?? 0;
                  const y = this.series.data[i]?.y ?? 0;
                  if (x > maxX) {
                    maxX = x;
                  }
                  if (y > maxY) {
                    maxY = y;
                  }
                  if (x < minX) {
                    minX = x;
                  }
                  if (y < minY) {
                    minY = y;
                  }
                }

                if (regionSet === -1 && setXYLimit) {
                  setXYLimit({
                    incUnitsMin: minY,
                    incUnitsMax: maxY,
                    incContributionMin: minX,
                    incContributionMax: maxX,
                  });
                  setRegionSet(this.index);
                } else if (setXYLimit) {
                  setXYLimit(null);
                  setRegionSet(-1);
                }
                return;
              }
              // @ts-expect-error Getting type error here, so skipping
              const url = `/user/_pricing/plan/${this.planId}`;
              window.open(url, "_blank");
            },
          },
        },
      },
    },
    series: series,
  };

  return <HighchartsReact highcharts={Highcharts} options={scatterOptions} />;
};

const IncrementalGCDChart = ({ dashboardData, isLoading, enrollDate }: ChartProps) => {
  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  const categories = dashboardData.date
    .map((d) => {
      const date = moment(d.name);
      if (date.isAfter(enrollDate)) {
        return d.name;
      } else {
        return null;
      }
    })
    .filter((d) => d !== null);

  const incValue = dashboardData.date.map((d) => d.inc_contribution);
  const minIncValue = incValue.reduce((a, b) => Math.min(a, b), 0);
  const asp = dashboardData.date.map((d) => d.asp);
  const referenceCR = dashboardData.date.map((d) => d.reference_cr);
  const maxCR = referenceCR.reduce((a, b) => Math.max(a, b), 0);
  const maxASP = asp.reduce((a, b) => Math.max(a, b), 0);

  const options = {
    chart: {
      height: 340,
      type: "column",
      zoomType: "x",
      style: {
        fontSize: "14px",
      },
    },
    title: {
      text: null,
    },
    yAxis: [
      {
        title: {
          text: null,
        },
        min: minIncValue,
        minimum: minIncValue,
      },
      {
        title: {
          text: "Reference CR",
        },
        opposite: true,
        min: 0,
        max: maxCR,
        tickInterval: 0.025,
        labels: {
          formatter: function () {
            // @ts-expect-error Getting type error here, so skipping
            return formatPercent(this.value, 2);
          },
        },
      },
      {
        title: {
          text: null,
        },
        opposite: true,
        min: 0,
        max: maxASP,
        tickInterval: 0.025,
        labels: {
          enabled: false,
        },
      },
    ],
    xAxis: {
      title: {
        text: null,
      },
      labels: {
        enabled: false,
      },
      categories: categories,
    },
    legend: {
      enabled: true,
      verticalAlign: "bottom",
    },
    series: [
      {
        name: "Incremental GCD",
        data: incValue,
        color: trellisPalette[4],
        opacity: 1,
        lineWidth: 4,
        type: "spline",
        yAxis: 0,
        tooltip: {
          valuePrefix: "$",
        },
      },
      {
        name: "Contribution",
        data: incValue,
        color: trellisPalette[4],
        opacity: 0.2,
        lineWidth: 3,
        type: "area",
        yAxis: 0,
        tooltip: {
          valuePrefix: "$",
        },
        showInLegend: false,
      },
      {
        name: "Reference CR",
        data: referenceCR,
        color: trellisPalette[5],
        type: "spline",
        yAxis: 1,
        tooltip: {
          valuePrefix: "",
          // @ts-expect-error Getting type error here, so skipping
          formatter: function () {
            // @ts-expect-error Getting type error here, so skipping
            return `${this.x}<br/>${this.series.name}: ${formatPercent(this.y, 2)}`;
          },
        },
      },
      {
        name: "ASP",
        data: asp,
        color: trellisPalette[3],
        type: "spline",
        yAxis: 2,
        visible: false,
        tooltip: {
          valuePrefix: "$",
          // @ts-expect-error Getting type error here, so skipping
          formatter: function () {
            // @ts-expect-error Getting type error here, so skipping
            return `${this.x}<br/>${this.series.name}: ${formatCurrency(this.y, "USA")}`;
          },
        },
      },
    ],

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
            maxHeight: 200,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
          },
        },
      ],
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const InventoryAwareChart = ({
  inventoryMetrics,
  setHealthSelection,
}: {
  inventoryMetrics: GroupedStats;
  setHealthSelection: (health: string | undefined) => void;
  healthSelection: string | undefined;
}) => {
  const bucketName: string = "count";

  const dates = Object.keys(inventoryMetrics).sort();

  const series = ["healthy", "excess", "overstock", "deficient"].map((healthType, idx) => {
    return {
      name: healthType.charAt(0).toUpperCase() + healthType.slice(1),
      color: trellisPalette[idx],
      data: dates.map((date) => {
        const ht = healthType as keyof (typeof inventoryMetrics)[string]["health"];
        const keys = Object.keys(inventoryMetrics[date]?.health ?? {});
        if (keys.includes(ht)) {
          return (
            inventoryMetrics[date]?.health[healthType as keyof (typeof inventoryMetrics)[string]["health"]][
              bucketName as keyof (typeof inventoryMetrics)[string]["health"]["healthy"]
            ] || 0
          );
        } else {
          return 0;
        }
      }),
      stack: "health",
    };
  });

  const options = {
    chart: {
      type: "column",
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
      height: 435,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      categories: dates,
      crosshair: true,
      accessibility: {
        description: "Report Dates",
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: undefined,
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
      },
      series: {
        groupPadding: 0,
        events: {
          click: function (event: Highcharts.SeriesClickEventObject) {
            const seriesName: string = event.point.series.name.toLowerCase();
            setHealthSelection(seriesName);
          },
        },
      },
    },
    series,
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const SalesForecast = ({
  dashboardData,
  marketPlace,
}: {
  dashboardData: {
    total: DashboardStats;
  };
  marketPlace: string;
}) => {
  return (
    <Col>
      {dashboardData.total.inc_cashflow > 0 ?
        <div className="fs-4">Sales Above Forecast</div>
      : <div className="fs-4">Sales Below Forecast</div>}
      <div className={`fw-bold`} style={{ color: "#000", fontSize: "3rem" }}>
        {formatCurrency(dashboardData.total.inc_cashflow, marketPlace, false, true, true)}
      </div>
    </Col>
  );
};

const ExtendedMetricCards = ({
  singlePlan,
  dashboardData,
  fetching,
}: {
  singlePlan: boolean;
  dashboardData: {
    total: DashboardStats;
  };
  fetching: boolean;
}) => {
  return (
    <>
      <Col>
        {singlePlan && <div className="fs-4">Reference CR</div>}
        {!singlePlan && <div className="fs-4">CR</div>}
        <div className="fs-2 fw-bold" style={{ color: "#777" }}>
          {fetching ? "-" : formatPercent(dashboardData.total.reference_cr, 2)}
        </div>
      </Col>
      <Col>
        <div className="fs-4">Page Views</div>
        <div className="fs-2 fw-bold" style={{ color: "#777" }}>
          {fetching ?
            "-"
          : formatNumber(dashboardData.total.page_views, {
              decimalPlaces: 0,
            })
          }
        </div>
      </Col>
    </>
  );
};

export function MetricCards({
  dashboardData,
  isLoading,

  marketPlace,
  fetching,
  singlePlan,
  pricingPlan,
  scatterData,
  setXYLimit,
  selectedPlanStrategy,
  inventoryMetrics,
  setHealthSelection,
  healthSelection,
}: Props) {
  const [priceOption, setPriceOption] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Row>
      <Col className="mb-1 px-4" xs={isExpanded ? 12 : 7}>
        <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
          <Card.Body className="p-4">
            <Row className="text-center fs-standard">
              <Col>
                {dashboardData && dashboardData.total.inc_contribution > 0 ?
                  <div className="fs-4">Incremental GCD from Price Increases</div>
                : <div className="fs-4">Volume Retention Cost</div>}
                <div className="fw-bold" style={{ fontSize: "3rem" }}>
                  {dashboardData === undefined || fetching ?
                    "-"
                  : formatCurrency(dashboardData.total.inc_contribution, marketPlace, false, true, false)}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {!isExpanded && (
          <>
            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    {dashboardData === undefined || fetching ?
                      "-"
                    : <SalesForecast dashboardData={dashboardData} marketPlace={marketPlace} />}
                  </Col>
                  <Col>
                    <div className="fs-4">Units Above Forecast</div>
                    <div className="fw-bold" style={{ color: "#000", fontSize: "3rem" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatNumber(dashboardData.total.inc_units, {
                          addPlus: true,
                        })
                      }
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    <div className="fs-4">Total Sales</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.total_sales, marketPlace, false, false, true)}
                    </div>
                  </Col>
                  <Col>
                    <div className="fs-4">Units Sold</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ? "-" : formatNumber(dashboardData.total.total_units)}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  <Col>
                    <div className="fs-4">Forecasted Sales</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.target_sales, marketPlace, false, false, true)}
                    </div>
                  </Col>
                  <Col>
                    <div className="fs-4">Forecasted Units</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ? "-" : formatNumber(dashboardData.total.target_units)}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
              <Card.Body className="p-4">
                <Row className="text-center fs-standard">
                  {singlePlan && (
                    <Col>
                      <div className="fs-4">Price Changes</div>
                      <div
                        className="fs-2 fw-bold"
                        style={{ color: "#777", cursor: "pointer" }}
                        onClick={() => setPriceOption(!priceOption)}
                      >
                        {!priceOption ?
                          <>
                            <span className="px-2">
                              <span style={{ color: "#bbb" }}>
                                <IoArrowUpCircleSharp />
                              </span>
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatNumber(dashboardData.total.price_increased, { decimalPlaces: 0 })}
                            </span>
                            <span className="px-2">
                              <span style={{ color: "#bbb" }}>
                                <IoArrowDownCircleSharp />
                              </span>{" "}
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatNumber(dashboardData.total.price_decreased, { decimalPlaces: 0 })}
                            </span>
                          </>
                        : <>
                            <span className="px-2">
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatCurrency(dashboardData.total.price_increased_avg, marketPlace, false, true, false)
                              }
                            </span>{" "}
                            /{" "}
                            <span className="px-2">
                              {dashboardData === undefined || fetching ?
                                "-"
                              : formatCurrency(dashboardData.total.price_decreased_avg, marketPlace)}
                            </span>
                          </>
                        }
                      </div>
                    </Col>
                  )}
                  <Col>
                    <div className="fs-4">ASP</div>
                    <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                      {dashboardData === undefined || fetching ?
                        "-"
                      : formatCurrency(dashboardData.total.asp, marketPlace)}
                    </div>
                  </Col>
                  {singlePlan && (
                    <Col>
                      <div className="fs-4">Reference Price</div>
                      <div className="fs-2 fw-bold" style={{ color: "#777" }}>
                        {dashboardData === undefined || fetching ?
                          "-"
                        : formatCurrency(dashboardData.total.reference_price, marketPlace)}
                      </div>
                    </Col>
                  )}

                  {dashboardData && !singlePlan && (
                    <ExtendedMetricCards singlePlan={singlePlan} dashboardData={dashboardData} fetching={fetching} />
                  )}
                </Row>
              </Card.Body>
            </Card>
          </>
        )}
      </Col>

      <Col className="mb-1 px-4" xs={isExpanded ? 12 : 5}>
        <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
          <Card.Body className="p-4">
            <Flex
              gap="small"
              justifyContent={"space-between"}
              alignContent={"center"}
              paddingBottom={"medium"}
              wrap="wrap"
            >
              {selectedPlanStrategy !== "override_supply" && (
                <div className="fs-4 fw-bold">Dynamic Pricing Contribution</div>
              )}
              {selectedPlanStrategy === "override_supply" && <div className="fs-4 fw-bold">Inventory Health</div>}
              <span style={{ cursor: "pointer" }} onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ?
                  <BiCollapse />
                : <FaExpandArrowsAlt />}
              </span>
            </Flex>
            {selectedPlanStrategy !== "override_supply" && (
              <div>
                {dashboardData === undefined || fetching ?
                  <Loading fullPage={false} height={"365px"} />
                : <SimpleChart
                    dashboardData={dashboardData}
                    isLoading={isLoading}
                    marketPlace={marketPlace}
                    enrollDate={pricingPlan.enroll_date}
                    isExpanded={isExpanded}
                    scatterData={scatterData}
                    setXYLimit={setXYLimit}
                    singlePlan={singlePlan}
                  />
                }
              </div>
            )}

            {selectedPlanStrategy === "override_supply" && (
              <div>
                <InventoryAwareChart
                  inventoryMetrics={inventoryMetrics}
                  setHealthSelection={setHealthSelection}
                  healthSelection={healthSelection}
                />
              </div>
            )}
          </Card.Body>
        </Card>

        {singlePlan && (
          <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
            <Card.Body className="p-4">
              <Row className="text-center fs-standard">
                {dashboardData && (
                  <ExtendedMetricCards singlePlan={singlePlan} dashboardData={dashboardData} fetching={fetching} />
                )}
              </Row>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
}
